import {
  InputHTMLAttributes,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Tooltip } from 'antd';
import { FieldProps as FormikFieldProps } from 'formik';
import { ErrorWarning } from '@styled-icons/remix-line';
import theme from 'styles/theme';
import FeedbackMessage from '../FeedbackMessage';
import * as S from './styles';


export interface FieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'prefix'> {
  label?: string;
  required?: boolean;
  name: string;
  isValid?: boolean;
  feedbackMessage?: string | ReactNode;
  rightContent?: ReactNode;
  prefix?: ReactNode | string;
  mask?: string;
  currency?: boolean;
  tooltip?: string;
  variant?: 'primary' | 'secondary';
  innerRef?: React.RefObject<HTMLInputElement>;
  component?: any;
}

const Field = ({
  label,
  name,
  required,
  isValid,
  feedbackMessage,
  prefix,
  placeholder,
  rightContent,
  mask,
  currency,
  tooltip,
  variant = 'primary',
  inputMode,
  ...props
}: FieldProps) => {
  const rightContentRef = useRef<HTMLDivElement>(null);
  const prefixRef = useRef<HTMLDivElement>(null);
  const [prefixWidth, setPrefixWidth] = useState(0);
  const [rightContentWidth, setRightContentWidth] = useState(0);

  useEffect(() => {
    const getExtraContentsWidth = () => {
      const shouldRenderRightContent = rightContentRef?.current;
      const shouldRenderPrefix = prefixRef?.current;

      if (shouldRenderRightContent) {
        setRightContentWidth(rightContentRef?.current?.offsetWidth);
      }

      if (shouldRenderPrefix) {
        setPrefixWidth(prefixRef?.current?.offsetWidth);
      }
    };

    getExtraContentsWidth();
  }, [rightContentRef, prefixRef]);

  const shouldRenderVariant = mask || currency;

  const renderFieldVariants = ({ field }: FormikFieldProps) => {
    if (mask) {
      return (
        <S.Mask
          $prefixWidth={prefixWidth}
          $rightContentWidth={rightContentWidth}
          {...field}
          {...props}
          inputMode={inputMode}
          $isValid={isValid}
          placeholder={placeholder}
          name={name}
          mask={mask}
          variant={variant}
          maskChar=""
          disabled={!!props.disabled}
          type={props.type}
        />
      );
    }

    return (
      <S.Currency
        $prefixWidth={prefixWidth}
        $rightContentWidth={rightContentWidth}
        {...field}
        {...props}
        $isValid={isValid}
        placeholder={placeholder}
        name={name}
        thousandSeparator={true}
        inputMode="numeric"
        allowNegative={false}
        decimalScale={0}
        variant={variant}
        disabled={!!props.disabled}
      />
    );
  };

  return (
    <div>
      {label && (
        <S.LabelContainer>
          <S.Label htmlFor={name} aria-required={required} required={required}>
            {label}
          </S.Label>
          {tooltip && (
            <Tooltip title={tooltip}>
              <S.IconWrapper>
                <ErrorWarning color={theme.colors.neutralSilver60} size={24} />
              </S.IconWrapper>
            </Tooltip>
          )}
        </S.LabelContainer>
      )}

      <S.Container>
        {prefix && <S.Prefix ref={prefixRef}>{prefix}</S.Prefix>}

        <S.Input
          $prefixWidth={prefixWidth}
          $rightContentWidth={rightContentWidth}
          $isValid={isValid}
          variant={variant}
          placeholder={placeholder}
          name={name}
          {...(shouldRenderVariant && { children: renderFieldVariants })}
          {...props}
          disabled={!!props.disabled}
          type={props.type}
        />

        {rightContent && (
          <S.RightContent ref={rightContentRef}>{rightContent}</S.RightContent>
        )}
      </S.Container>

      {!isValid && feedbackMessage && (
        <FeedbackMessage marginTop={8}>{feedbackMessage}</FeedbackMessage>
      )}
    </div>
  );
};

export default Field;
